<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import BExpandablePill from "@/components/common/BExpandablePill.vue";
import type { ExpandablePillItem } from "@/components/common/BExpandablePill.vue";
import { useScreeningRejectionCategories } from "@/composables/recruitment/useScreeningRejectionCategories";
import { bgColors, size as sizeConst } from "@/services/application/constantes";
import MaterialSymbolsCancel from "~icons/material-symbols/cancel";
import MaterialSymbolsCheckRounded from "~icons/material-symbols/check-rounded";
import MaterialSymbolsDoNotDisturbOnOutlineRounded from "~icons/material-symbols/do-not-disturb-on-outline-rounded";

// The reasons in the same card as « Décider » on the process: one click sets the candidate « À rejeter » with it
const props = withDefaults(
  defineProps<{
    /** The reason the candidate already has, ticked in the list */
    selectedId?: string | null;
    bg?: keyof typeof bgColors;
    size?: keyof typeof sizeConst;
    buttonClass?: string;
    buttonContainerClass?: string;
  }>(),
  {
    selectedId: undefined,
    bg: "base",
    size: "dense",
    buttonClass: "",
    buttonContainerClass: "",
  },
);

const emits = defineEmits<{
  select: [categoryId: string | null];
}>();

const { t } = useI18n();
const { categories } = useScreeningRejectionCategories();

const items = computed<ExpandablePillItem[]>(() => [
  ...categories.value.map((category) => {
    const isSelected = category._id === props.selectedId;
    return {
      _id: `reason-${category._id}`,
      icon: isSelected ? MaterialSymbolsCheckRounded : MaterialSymbolsCancel,
      title: category.display,
      tooltip: category.display,
      color: isSelected ? ("error" as const) : ("base" as const),
      onClick: () => emits("select", category._id),
    };
  }),
  {
    _id: "reason-none",
    icon: props.selectedId === null ? MaterialSymbolsCheckRounded : MaterialSymbolsDoNotDisturbOnOutlineRounded,
    title: t("modules.recruitment.screening.rejectReasons.noReason"),
    color: props.selectedId === null ? ("error" as const) : ("base" as const),
    onClick: () => emits("select", null),
  },
]);
</script>

<template>
  <BExpandablePill
    :label="t('modules.recruitment.screening.rejectReasons.title')"
    :items="items"
    :bg="bg"
    :size="size"
    :button-class="buttonClass"
    :button-container-class="buttonContainerClass"
    placement="bottom-end"
  >
    <template #button>
      <slot />
    </template>
  </BExpandablePill>
</template>
